import { useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '@user/store/hooks'

import { fetchTerritoryBoundaries } from '../api'
import {
  findTerritoryOverlaps,
  OverlapCandidate,
  TerritoryOverlap,
} from '../overlapDetection'
import {
  selectTerritoryBoundariesById,
  selectTerritoryLoadingIds,
  selectTerritorySummaries,
} from '../slice'
import { TerritoryBoundary } from '../typings'

export type OverlapStatus =
  /** Geometry is still loading; no verdict yet. */
  | 'checking'
  /** Every candidate was compared. An empty list here really does mean no overlap. */
  | 'complete'
  /** Some territories could not be compared, so the list may be short. */
  | 'incomplete'

export type OverlapResult = {
  overlaps: TerritoryOverlap[]
  status: OverlapStatus
}

/**
 * Which existing territories the shape being drawn collides with (WWM-17508).
 *
 * Mostly this answers instantly: the territories you could overlap are the ones on screen, and
 * their geometry is already cached by the viewport fetch. The loose end is drawing before that
 * fetch lands, so anything still missing is requested here -- once per distinct set of ids,
 * keyed off a joined string rather than the rebuilt-every-render array. A failed request
 * releases its ids, so depending on that array would turn one server failure into an endless
 * retry loop with a dialog open on top of it.
 *
 * 'checking' versus 'incomplete' keys off the slice's `loadingIds` rather than off the dispatch,
 * because that is the only signal that re-renders on BOTH outcomes: it is cleared by `rejected`
 * as well as `fulfilled`. Keying off "have we dispatched" instead would leave the warning stuck
 * on "checking" forever whenever the request failed -- no state changes, so nothing re-renders
 * to correct it.
 */
export function useTerritoryOverlaps(
  boundary: TerritoryBoundary,
  excludeId?: number | null,
): OverlapResult {
  const dispatch = useAppDispatch()
  const summaries = useSelector(selectTerritorySummaries)
  const boundariesById = useSelector(selectTerritoryBoundariesById)
  const loadingIds = useSelector(selectTerritoryLoadingIds)

  const candidates = useMemo<OverlapCandidate[]>(
    () =>
      summaries.map((summary) => ({
        id: summary.id,
        name: summary.name,
        bounds: summary.bounds,
        boundary: boundariesById[summary.id] ?? null,
      })),
    [summaries, boundariesById],
  )

  const scan = useMemo(
    () => findTerritoryOverlaps(boundary, candidates, { excludeId }),
    [boundary, candidates, excludeId],
  )

  // A string, not the array: the array is rebuilt every render, so depending on it directly
  // would re-request on every render. Keyed this way the effect fires once per genuine change
  // of ids -- which is what stops a rejected fetch (it releases its ids, so they show up as
  // missing again) from becoming an endless retry loop with a dialog open on top of it.
  const pendingKey = scan.pendingIds.join(',')

  // The last set of ids actually asked for, which is what separates "not asked yet" from
  // "asked, and it did not come back". Not a guard against re-dispatching -- the dependency
  // above already does that.
  const requestedKeyRef = useRef<string | null>(null)

  // Read through a ref inside the effect rather than listed as a dependency: loadingIds changes
  // as every request settles, and depending on it would re-run this on each one.
  const loadingIdsRef = useRef(loadingIds)
  loadingIdsRef.current = loadingIds

  useEffect(() => {
    if (!pendingKey) return

    // Recorded even when nothing is dispatched below, because it answers "have we decided what
    // to do about this set of ids", not "did we send a request".
    requestedKeyRef.current = pendingKey

    // Ids already in flight are skipped. useTerritoryLayer fetches the viewport's geometry, and
    // drawing before that lands put the same ids on a second request. Two copies of one request
    // is wasteful on its own, but the real damage is on failure: whichever rejects first removes
    // the ids from loadingIds while the other is still out, and the overlap warning flickers to
    // 'incomplete' on a check that is in fact still running.
    //
    // The full set still drives `status` below -- an id someone else is fetching is every bit as
    // unresolved as one we asked for ourselves.
    const alreadyInFlight = new Set(loadingIdsRef.current)
    const toRequest = pendingKey
      .split(',')
      .map(Number)
      .filter((id) => !alreadyInFlight.has(id))

    if (!toRequest.length) return

    dispatch(fetchTerritoryBoundaries(toRequest))
  }, [dispatch, pendingKey])

  let status: OverlapStatus = 'complete'

  if (pendingKey) {
    const loading = new Set(loadingIds)
    // The ref covers the single render between "we know geometry is missing" and the effect
    // that asks for it, where nothing is in flight yet but giving up would be wrong.
    const aboutToRequest = requestedKeyRef.current !== pendingKey
    const inFlight = scan.pendingIds.some((id) => loading.has(id))

    status = aboutToRequest || inFlight ? 'checking' : 'incomplete'
  } else if (scan.failedIds.length) {
    status = 'incomplete'
  }

  return { overlaps: scan.overlaps, status }
}
